<template>
  <AppLayout>
    <Head title="Zarchiwizowane tablice" />

    <div class="col-12 layout-spacing">
      <div class="widget widget-six">

        <!-- Nagłówek -->
        <div class="widget-heading">
          <div class="row align-items-center">
            <div class="col-12 col-lg-7">
              <h4 class="mb-2">
                <i class="bi bi-archive me-2"></i>
                Zarchiwizowane tablice
              </h4>

              <p class="text-muted mb-0">
                Przeglądaj zarchiwizowane tablice i przywracaj je,
                gdy będą ponownie potrzebne.
              </p>
            </div>

            <div class="col-12 col-lg-5 mt-3 mt-lg-0 text-lg-end">
              <Link
                href="/kanban/boards"
                class="btn btn-outline-secondary btn-sm"
              >
                <i class="bi bi-arrow-left me-1"></i>
                Wróć do tablic
              </Link>
            </div>
          </div>
        </div>

        <!-- Lista tablic -->
        <div class="widget-content">
          <div class="custom-table">

            <div
              class="d-flex align-items-center justify-content-between mb-3"
            >
              <h5 class="mb-0">
                Archiwum
              </h5>

              <span class="badge bg-secondary">
                {{ boards.total ?? boards.data.length }}
              </span>
            </div>

            <!-- Pusta lista -->
            <div
              v-if="boards.data.length === 0"
              class="text-center py-5"
            >
              <i class="bi bi-archive activity-empty-icon"></i>

              <h5 class="mt-3">
                Brak zarchiwizowanych tablic
              </h5>

              <p class="text-muted mb-3">
                Wszystkie Twoje tablice są aktywne albo nie zostały
                jeszcze utworzone.
              </p>

              <Link
                href="/kanban/boards"
                class="btn btn-primary btn-sm"
              >
                <i class="bi bi-kanban me-1"></i>
                Przejdź do tablic
              </Link>
            </div>

            <!-- Tabela -->
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th style="width: 60px">#</th>
                    <th>Nazwa tablicy</th>
                    <th>Utworzył</th>
                    <th>Data archiwizacji</th>
                    <th class="text-end">Akcje</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(board, index) in boards.data"
                    :key="board.id"
                  >
                    <td>
                      {{ (boards.from ?? 1) + index }}
                    </td>

                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <span class="archived-board-icon">
                          <i class="bi bi-kanban"></i>
                        </span>

                        <div>
                          <div class="fw-semibold">
                            {{ board.name }}
                          </div>

                          <div
                            v-if="board.description"
                            class="text-muted small mt-1 archived-description"
                          >
                            {{ board.description }}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span class="text-muted">
                        {{
                          board.creator?.name ??
                          'Nieznany użytkownik'
                        }}
                      </span>
                    </td>

                    <td>
                      <span class="text-muted small">
                        <i class="bi bi-calendar3 me-1"></i>
                        {{ formatDate(board.archived_at) }}
                      </span>
                    </td>

                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-success btn-sm"
                        :disabled="restoringId === board.id"
                        @click="restoreBoard(board)"
                      >
                        <i
                          :class="restoringId === board.id
                            ? 'bi bi-arrow-repeat me-1'
                            : 'bi bi-arrow-counterclockwise me-1'"
                        ></i>

                        {{
                          restoringId === board.id
                            ? 'Przywracanie...'
                            : 'Przywróć'
                        }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>

          <!-- Paginacja -->
          <nav
            v-if="boards.links?.length > 3"
            class="mt-4"
            aria-label="Stronicowanie zarchiwizowanych tablic"
          >
            <div
              class="d-flex flex-wrap align-items-center justify-content-between gap-3"
            >
              <p class="text-muted small mb-0">
                <template v-if="boards.total">
                  Wyświetlono
                  {{ boards.from ?? 0 }}–{{ boards.to ?? 0 }}
                  z {{ boards.total }} tablic
                </template>

                <template v-else>
                  Lista zarchiwizowanych tablic
                </template>
              </p>

              <div class="d-flex flex-wrap gap-1">
                <template
                  v-for="(link, index) in boards.links"
                  :key="index"
                >
                  <Link
                    v-if="link.url"
                    :href="link.url"
                    preserve-scroll
                    class="btn btn-sm"
                    :class="link.active
                      ? 'btn-primary'
                      : 'btn-outline-secondary'"
                    v-html="link.label"
                  />

                  <span
                    v-else
                    class="btn btn-sm btn-outline-secondary disabled"
                    v-html="link.label"
                  />
                </template>
              </div>
            </div>
          </nav>

        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/Layouts/App.vue'
import { Head, Link, router } from '@inertiajs/vue3'

export default {
  components: {
    AppLayout,
    Head,
    Link,
  },

  props: {
    boards: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      restoringId: null,
    }
  },

  methods: {
    restoreBoard(board) {
      if (this.restoringId !== null) return

      if (!confirm(`Przywrócić tablicę „${board.name}”?`)) {
        return
      }

      this.restoringId = board.id

      router.patch(
        `/kanban/boards-all/${board.id}/restore`,
        {},
        {
          preserveScroll: true,

          onFinish: () => {
            this.restoringId = null
          },
        }
      )
    },

    formatDate(value) {
      if (!value) return '—'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return '—'
      }

      return new Intl.DateTimeFormat('pl-PL', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date)
    },
  },
}
</script>

<style scoped>
.archived-board-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 1.1rem;
}

.archived-description {
  max-width: 350px;
  overflow-wrap: anywhere;
}

.activity-empty-icon {
  font-size: 2.5rem;
  color: #adb5bd;
}

@media (max-width: 767.98px) {
  .archived-description {
    max-width: 220px;
  }
}
</style>